import { Flex, Pagination, Table, type TableColumnType, Typography } from 'antd';
import capitalize from 'lodash/capitalize';
import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';



import { useAdminCompanyController_findAll } from '@api-client/generated/AdminCompanyController/findAll';
import { Schemas } from '@api-client/generated/types';
import { CompanyStatus, SupportedCountries } from '@constants';
import { FilterConstructor } from '@entities';
import { getSearchParams, transformToOptions } from '@utils';



import * as S from './styled';


const { Title } = Typography;

const statusOptions = [
  { label: 'All Statuses', value: '' },
  ...transformToOptions(CompanyStatus),
];

const countryOptions = [
  { label: 'All Countries', value: '' },
  { label: 'Luxembourg', value: SupportedCountries.Luxembourg },
  { label: 'France', value: SupportedCountries.France },
  { label: 'Belgium', value: SupportedCountries.Belgium },
];

type Company = Schemas.Company;
type FilterOptions = Record<string, unknown>;

const Companies: FC = () => {
  const { search } = useLocation();
  const searchParams = getSearchParams<{ [key: string]: string }>(search);
  const [filterOptions, setFilterOptions] =
    useState<FilterOptions>(searchParams);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: companiesData, isPending: loading } =
    useAdminCompanyController_findAll({
      params: {
        page: currentPage,
        ...filterOptions,
      },
    });

  const companies = companiesData?.records || [];
  const metadata = companiesData?.metadata;

  // Reset to first page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterOptions]);

  const columns: TableColumnType<Schemas.AdminCompanyResponseDto>[] = [
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Company',
      width: 160,
      render: (name, record) => (
        <S.CompanyLink to={record.id}>{name}</S.CompanyLink>
      ),
    },
    {
      key: 'country',
      dataIndex: ['details', 'address', 'countryCode'],
      title: 'Country',
      width: 120,
      render: (countryCode) => {
        const countryOption = countryOptions.find(
          (option) => option.value === countryCode
        );
        return countryOption?.label || countryCode || '-';
      },
    },
    {
      key: 'status',
      dataIndex: 'status',
      title: 'Status',
      width: 160,
      render: (status) => {
        const formattedStatus = capitalize(status.split('_').join(' '));
        return <S.StatusTag status={status}>{formattedStatus}</S.StatusTag>;
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Flex gap={24} vertical>
      <Title>Companies</Title>
      <S.FilterWrapper>
        <FilterConstructor<Company>
          controls={{
            left: [
              {
                type: 'search',
                formName: 'term',
                params: {
                  placeholder: 'Search companies',
                },
              },
              {
                type: 'divider',
              },
            ],
            right: [
              {
                type: 'select',
                formName: 'status',
                params: {
                  width: 150,
                  size: 'large',
                  placeholder: 'All statuses',
                  options: statusOptions,
                  defaultValue: '',
                },
              },
              {
                type: 'select',
                formName: 'countryCode',
                params: {
                  width: 150,
                  size: 'large',
                  placeholder: 'All countries',
                  options: countryOptions,
                  defaultValue: '',
                },
              },
              {
                type: 'divider',
              },
              {
                type: 'custom-component',
                component: (
                  <Typography.Text>
                    {!metadata ? '—' : (metadata?.totalRecords ?? '—')}{' '}
                    companies
                  </Typography.Text>
                ),
              },
            ],
          }}
          onChange={setFilterOptions}
          withSearchParams
        />
      </S.FilterWrapper>

      <S.TableContainer>
        <Table
          rowKey={({ id }) => id}
          dataSource={companies}
          columns={columns}
          loading={loading}
          pagination={false}
          scroll={{ x: 720 }}
        />
        {metadata && metadata.totalPages > 1 && (
          <Flex justify="end" style={{ marginTop: 24 }}>
            <Pagination
              current={metadata.currentPage}
              pageSize={metadata.perPage}
              total={metadata.totalRecords}
              onChange={handlePageChange}
              showSizeChanger={false}
            />
          </Flex>
        )}
      </S.TableContainer>
    </Flex>
  );
};

export default Companies;